import React, { useState, useEffect } from "react"
import { Label, Button } from "ksc-portal-common-components/shadcn"
import PasswordInput from "./PasswordInput"

type SetErrorFunction = (error: string) => void

export const checkCriteria = ({
  value,
  setError,
  passwordCriteria: { length, lowerCase, upperCase, numbers, symbols },
}: {
  value: string
  setError: SetErrorFunction
  passwordCriteria: {
    length: number
    lowerCase: boolean
    upperCase: boolean
    numbers: boolean
    symbols: boolean
  }
}) => {
  if (value.length < length)
    return setError(`Password must be at least ${length} characters long.`)
  if (upperCase && value.toLowerCase() === value)
    return setError("Password must contain an uppercase character.")
  if (lowerCase && value.toUpperCase() === value)
    return setError("Password must contain a lowercase character.")
  if (numbers && !/\d/.test(value))
    return setError("Password must contain a number.")
  if (symbols && /^[a-z0-9£]+$/i.test(value))
    return setError("Password must contain a special character.")
  return setError("")
}

export const checkMatch = ({
  values,
  setError,
}: {
  values: [string, string]
  setError: SetErrorFunction
}) => setError(values[0] === values[1] ? "" : "Passwords must match")

interface ConfirmPasswordsProps {
  passwordCriteria: {
    length: number
    lowerCase: boolean
    upperCase: boolean
    numbers: boolean
    symbols: boolean
  }
  storePasswordCriteria: () => void
  enterNewPassword: (password: string) => void
}

const ConfirmPasswords = ({
  passwordCriteria,
  storePasswordCriteria,
  enterNewPassword,
}: ConfirmPasswordsProps) => {
  const [firstPassword, setFirstPassword] = useState("")
  const [secondPassword, setSecondPassword] = useState("")
  const [firstPasswordError, setFirstPasswordError] = useState("")
  const [secondPasswordError, setSecondPasswordError] = useState("")

  const checkFirst = () =>
    passwordCriteria &&
    checkCriteria({
      value: firstPassword,
      passwordCriteria,
      setError: setFirstPasswordError,
    })
  const checkSecond = () =>
    checkMatch({
      values: [firstPassword, secondPassword],
      setError: setSecondPasswordError,
    })
  const submitPassword = (e: React.FormEvent) => {
    e.preventDefault()
    const password = firstPassword
    if (!!password && !firstPasswordError && !secondPasswordError)
      enterNewPassword(password)
  }

  useEffect(() => {
    if (!passwordCriteria) storePasswordCriteria()
  }, [passwordCriteria])

  useEffect(() => {
    if (firstPassword) checkFirst()
    if (secondPassword) checkSecond()
  }, [firstPassword, secondPassword])

  return (
    <form onSubmit={submitPassword}>
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="new-password">New Password</Label>
          <PasswordInput
            id="new-password"
            required
            onChange={({ target: { value } }) => {
              setFirstPassword(value)
              checkFirst()
            }}
            value={firstPassword}
            data-testid="new-password-input"
            className={`${firstPasswordError ? "border-destructive" : ""}`}
          />
          {firstPasswordError && (
            <p className="text-destructive text-sm">{firstPasswordError}</p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm-password">Confirm Password</Label>
          <PasswordInput
            id="confirm-password"
            required
            onChange={({ target: { value } }) => {
              setSecondPassword(value)
              checkSecond()
            }}
            value={secondPassword}
            data-testid="confirm-password-input"
            className={`${secondPasswordError ? "border-destructive" : ""}`}
          />
          {secondPasswordError && (
            <p className="text-destructive text-sm">{secondPasswordError}</p>
          )}
        </div>
      </div>
      <Button
        variant="default"
        className="w-full mt-4"
        disabled={
          !firstPassword || !!(firstPasswordError || secondPasswordError)
        }
        data-testid="submit-button"
      >
        Submit
      </Button>
    </form>
  )
}

export default ConfirmPasswords
